import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HelpCircle,
  Book,
  MessageCircle,
  Mail,
  Phone,
  ChevronDown,
  ExternalLink,
  Search,
  ArrowLeft,
  Save,
  PlayCircle,
  FileText,
  Users,
  Shield,
  Settings,
  CreditCard,
  Star,
  CheckCircle,
  Clock,
  Zap,
  Download,
  Video,
  Monitor,
  Smartphone,
  Globe,
  Camera,
  Package,
  TrendingUp,
  BarChart3,
  Calendar,
  Bell,
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  Target,
  Award,
  Map,
  ChevronRight,
  RefreshCw,
  Headphones,
  MessageSquare,
  Send
} from 'lucide-react';

const HelpSupport = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('faq');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = [
    { id: 'all', name: 'Tümü', icon: HelpCircle, color: 'blue' },
    { id: 'getting-started', name: 'Başlangıç', icon: Zap, color: 'green' },
    { id: 'features', name: 'Özellikler', icon: Settings, color: 'purple' },
    { id: 'account', name: 'Hesap Yönetimi', icon: Users, color: 'orange' },
    { id: 'billing', name: 'Ödeme & Faturalar', icon: CreditCard, color: 'red' },
    { id: 'security', name: 'Güvenlik', icon: Shield, color: 'teal' },
    { id: 'mobile', name: 'Mobil Uygulama', icon: Smartphone, color: 'pink' },
    { id: 'integration', name: 'Entegrasyonlar', icon: Globe, color: 'indigo' }
  ];

  const faqs = [
    {
      id: 1,
      category: 'getting-started',
      question: 'İş Takip Otomasyonu sistemine nasıl başlarım?',
      answer: 'Sisteme başlamak için öncelikle ücretsiz hesap oluşturun. E-posta adresinizi doğruladıktan sonra profil bilgilerinizi tamamlayın. İlk projenizi oluşturmak için ana sayfadaki "Yeni Proje" butonuna tıklayın. Sistem size adım adım rehberlik edecektir.',
      popular: true,
      tags: ['başlangıç', 'hesap', 'proje']
    },
    {
      id: 2,
      category: 'features',
      question: 'Görev atama nasıl yapılır?',
      answer: 'Görev oluşturmak için "Görevler" sekmesinden "Yeni Görev" butonuna tıklayın. Görev başlığı, açıklama, son tarih ve öncelik seviyesini belirleyin. Atanacak kişiyi seçmek için "@" işaretini kullanarak takım üyelerini etiketleyebilirsiniz.',
      popular: true,
      tags: ['görev', 'atama', 'takım']
    },
    {
      id: 3,
      category: 'account',
      question: 'Şifremi unuttum, nasıl sıfırlayabilirim?',
      answer: 'Giriş sayfasında "Şifremi Unuttum" linkine tıklayın. Kayıtlı e-posta adresinizi girin, size şifre sıfırlama linki gönderilecektir. Link 24 saat geçerlidir.',
      popular: false,
      tags: ['şifre', 'sıfırlama', 'güvenlik']
    },
    {
      id: 4,
      category: 'features',
      question: 'Dosya paylaşımı nasıl yapılır?',
      answer: 'Görev detaylarında veya proje sayfasında dosya yükleme alanını kullanabilirsiniz. Maksimum 50MB boyutunda dosyalar yükleyebilirsiniz. Desteklenen formatlar: PDF, DOC, XLS, PPT, JPG, PNG.',
      popular: true,
      tags: ['dosya', 'paylaşım', 'yükleme']
    },
    {
      id: 5,
      category: 'billing',
      question: 'Planımı nasıl yükseltebilirim?',
      answer: 'Hesap ayarlarından "Abonelik" bölümüne gidin. Mevcut planınızı ve diğer seçenekleri görebilirsiniz. İstediğiniz planı seçip ödeme bilgilerinizi güncelleyin. Değişiklik anında aktif olur.',
      popular: true,
      tags: ['plan', 'yükseltme', 'ödeme']
    },
    {
      id: 6,
      category: 'security',
      question: 'İki faktörlü kimlik doğrulama nasıl aktif edilir?',
      answer: 'Güvenlik ayarlarından "İki Faktörlü Doğrulama" seçeneğini bulun. QR kodunu Google Authenticator veya Microsoft Authenticator ile tarayın. Doğrulama kodunu girerek aktivasyonu tamamlayın.',
      popular: false,
      tags: ['2fa', 'güvenlik', 'doğrulama']
    },
    {
      id: 7,
      category: 'features',
      question: 'Rapor nasıl oluşturulur?',
      answer: 'Raporlar sekmesinden "Yeni Rapor" butonuna tıklayın. Rapor türünü seçin (görev, zaman, performans). Tarih aralığını belirleyin ve filtreleri uygulayın. "Rapor Oluştur" ile Excel veya PDF formatında indirin.',
      popular: true,
      tags: ['rapor', 'analiz', 'veri']
    },
    {
      id: 8,
      category: 'mobile',
      question: 'Mobil uygulamayı nereden indirebilirim?',
      answer: 'iOS için App Store\'dan, Android için Google Play Store\'dan "İş Takip Otomasyonu" uygulamasını arayın. QR kod ile hızlı indirme linkleri web sitesinin alt kısmında bulunmaktadır.',
      popular: false,
      tags: ['mobil', 'uygulama', 'indirme']
    },
    {
      id: 9,
      category: 'integration',
      question: 'Google Calendar entegrasyonu nasıl yapılır?',
      answer: 'Entegrasyonlar sayfasından Google Calendar seçeneğini bulun. "Bağlan" butonuna tıklayıp Google hesabınızla yetkilendirme yapın. Görevleriniz otomatik olarak takviminize aktarılacaktır.',
      popular: false,
      tags: ['entegrasyon', 'takvim', 'google']
    },
    {
      id: 10,
      category: 'account',
      question: 'Bildirim ayarlarını nasıl özelleştiririm?',
      answer: 'Hesap ayarlarından "Bildirimler" bölümüne gidin. E-posta, masaüstü ve mobil bildirimlerini ayrı ayrı yapılandırabilirsiniz. Belirli etkinlikler için özel ayarlar yapabilirsiniz.',
      popular: true,
      tags: ['bildirim', 'ayarlar', 'özelleştirme']
    }
  ];

  const videoTutorials = [
    {
      id: 1,
      title: 'Sisteme Hızlı Başlangıç',
      description: 'İlk 10 dakikada sistemi kurun ve kullanmaya başlayın',
      duration: '8:45',
      thumbnail: '/api/placeholder/320/180',
      category: 'getting-started',
      views: '12.4k'
    },
    {
      id: 2,
      title: 'Görev Yönetimi ve Atama',
      description: 'Görev oluşturma, atama ve takip süreçleri',
      duration: '15:20',
      thumbnail: '/api/placeholder/320/180',
      category: 'features',
      views: '8.7k'
    },
    {
      id: 3,
      title: 'Rapor ve Analitik Kullanımı',
      description: 'Detaylı raporlar oluşturma ve analiz yapma',
      duration: '12:15',
      thumbnail: '/api/placeholder/320/180',
      category: 'features',
      views: '6.2k'
    },
    {
      id: 4,
      title: 'Mobil Uygulama Rehberi',
      description: 'Mobil uygulamayı maksimum verimle kullanın',
      duration: '10:30',
      thumbnail: '/api/placeholder/320/180',
      category: 'mobile',
      views: '4.8k'
    }
  ];

  const quickGuides = [
    {
      id: 1,
      title: 'İlk Projenizi Oluşturun',
      steps: [
        'Ana sayfadan "Yeni Proje" butonuna tıklayın',
        'Proje adı ve açıklama girin',
        'Takım üyelerini davet edin',
        'İlk görevinizi oluşturun',
        'Projeyi başlatın'
      ],
      icon: Plus,
      color: 'green',
      estimatedTime: '5 dakika'
    },
    {
      id: 2,
      title: 'Takım Üyesi Davet Etme',
      steps: [
        'Proje ayarlarına gidin',
        '"Takım" sekmesini seçin',
        '"Üye Davet Et" butonuna tıklayın',
        'E-posta adresi ve rol seçin',
        'Davet gönder'
      ],
      icon: Users,
      color: 'blue',
      estimatedTime: '2 dakika'
    },
    {
      id: 3,
      title: 'Rapor Oluşturma',
      steps: [
        'Raporlar sekmesine gidin',
        'Rapor türünü seçin',
        'Tarih aralığını belirleyin',
        'Filtreleri uygulayın',
        'Raporu indirin'
      ],
      icon: BarChart3,
      color: 'purple',
      estimatedTime: '3 dakika'
    }
  ];

  const supportOptions = [
    {
      title: 'Canlı Destek',
      description: 'Uzman ekibimizle anlık görüşme',
      icon: MessageCircle,
      available: 'Hafta içi 09:00-18:00',
      responseTime: 'Anında',
      action: 'Sohbet Başlat',
      primary: true,
      status: 'online'
    },
    {
      title: 'E-posta Desteği',
      description: 'Detaylı sorular için yazılı destek',
      icon: Mail,
      available: '7/24 Destek',
      responseTime: '2-4 saat',
      action: 'E-posta Gönder',
      primary: false,
      status: 'available'
    },
    {
      title: 'Telefon Desteği',
      description: 'Acil durumlar için telefon hattı',
      icon: Phone,
      available: 'Hafta içi 09:00-17:00',
      responseTime: 'Anında',
      action: '0850 123 45 67',
      primary: false,
      status: 'available'
    },
    {
      title: 'Video Görüşme',
      description: 'Ekran paylaşımlı teknik destek',
      icon: Video,
      available: 'Randevu ile',
      responseTime: 'Planlanmış',
      action: 'Randevu Al',
      primary: false,
      status: 'scheduled'
    }
  ];

  const handleFaqClick = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const popularFaqs = faqs.filter(faq => faq.popular);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(-1)}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
                <ArrowLeft className="w-5 h-5" />
        </button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-white" />
                </div>
          <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">Yardım Merkezi</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Size nasıl yardımcı olabiliriz?</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="hidden md:flex items-center space-x-2 bg-green-50 dark:bg-green-900/30 px-3 py-1 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-green-700 dark:text-green-400 font-medium">Canlı Destek Aktif</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Search Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 mb-8 text-center"
        >
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Aradığınızı hızlıca bulun
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Sorularınızın cevaplarını anında keşfedin veya uzman ekibimizle iletişime geçin
          </p>
          
          <div className="relative max-w-2xl mx-auto mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
              placeholder="Sorunuzu buraya yazın... (örn: görev nasıl oluşturulur)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
      </div>

          {/* Quick Search Tags */}
          <div className="flex flex-wrap justify-center gap-2">
            {['görev oluşturma', 'şifre sıfırlama', 'takım davet', 'rapor indirme', 'mobil uygulama'].map((tag) => (
              <button
                key={tag}
                onClick={() => setSearchQuery(tag)}
                className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {tag}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { id: 'faq', name: 'Sık Sorulan Sorular', icon: HelpCircle },
            { id: 'guides', name: 'Hızlı Rehberler', icon: Book },
            { id: 'videos', name: 'Video Eğitimler', icon: PlayCircle },
            { id: 'support', name: 'Destek Seçenekleri', icon: Headphones }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.name}</span>
          </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Categories */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Kategoriler</h3>
              <div className="space-y-2">
                {categories.map((category) => {
                  const IconComponent = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all ${
                        activeCategory === category.id
                          ? `bg-${category.color}-50 dark:bg-${category.color}-900/30 text-${category.color}-700 dark:text-${category.color}-400 border border-${category.color}-200 dark:border-${category.color}-700`
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <IconComponent className="w-4 h-4" />
                      <span className="text-sm font-medium">{category.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">İstatistikler</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Toplam Makale</span>
                  <span className="font-semibold text-gray-900 dark:text-white">127</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Video Eğitim</span>
                  <span className="font-semibold text-gray-900 dark:text-white">24</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Ortalama Yanıt Süresi</span>
                  <span className="font-semibold text-green-600">2.4 saat</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Memnuniyet Oranı</span>
                  <span className="font-semibold text-green-600">98.7%</span>
                </div>
              </div>
            </div>
      </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* FAQ Tab Content */}
            {activeTab === 'faq' && (
              <div className="space-y-6">
                {/* Popular FAQs */}
                {activeCategory === 'all' && !searchQuery && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center space-x-2 mb-6">
                      <Star className="w-5 h-5 text-yellow-500" />
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">Popüler Sorular</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {popularFaqs.slice(0, 4).map((faq, index) => (
                        <button
                          key={faq.id}
                          onClick={() => {
                            setActiveCategory(faq.category);
                            setExpandedFaq(faqs.findIndex(f => f.id === faq.id));
                          }}
                          className="text-left p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          <h3 className="font-medium text-gray-900 dark:text-white mb-2">{faq.question}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{faq.answer}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* All FAQs */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {searchQuery ? 'Arama Sonuçları' : 'Sık Sorulan Sorular'}
        </h2>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {filteredFaqs.length} sonuç bulundu
                    </span>
                  </div>

        <div className="space-y-4">
          {filteredFaqs.map((faq, index) => (
                      <div
                        key={faq.id}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => handleFaqClick(index)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-white mb-1">{faq.question}</h3>
                            <div className="flex items-center space-x-2">
                              {faq.popular && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400">
                                  <Star className="w-3 h-3 mr-1" />
                                  Popüler
                                </span>
                              )}
                              <div className="flex space-x-1">
                                {faq.tags.slice(0, 3).map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded"
                                  >
                                    {tag}
                </span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedFaq === index ? 'rotate-180' : ''
                          }`} />
              </button>
                        
              {expandedFaq === index && (
                <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="bg-gray-50 dark:bg-gray-700/50 p-4 border-t border-gray-200 dark:border-gray-600"
                >
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{faq.answer}</p>
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                              <span className="text-sm text-gray-500 dark:text-gray-400">Bu yanıt yararlı oldu mu?</span>
                              <div className="flex space-x-2">
                                <button className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm rounded-md hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors">
                                  👍 Evet
                                </button>
                                <button className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm rounded-md hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors">
                                  👎 Hayır
                                </button>
                              </div>
                            </div>
                </motion.div>
              )}
                      </div>
                    ))}
                  </div>

                  {filteredFaqs.length === 0 && (
                    <div className="text-center py-12">
                      <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sonuç bulunamadı</h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-6">
                        Aradığınız terim için sonuç bulunamadı. Farklı anahtar kelimeler deneyin.
                      </p>
                      <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        Destek Ekibiyle İletişime Geç
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Guides Tab Content */}
            {activeTab === 'guides' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Hızlı Başlangıç Rehberleri</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {quickGuides.map((guide) => {
                      const IconComponent = guide.icon;
                      return (
                        <div
                          key={guide.id}
                          className={`p-6 border-2 border-${guide.color}-200 dark:border-${guide.color}-700 rounded-xl hover:shadow-lg transition-all bg-${guide.color}-50 dark:bg-${guide.color}-900/20`}
                        >
                          <div className={`w-12 h-12 bg-${guide.color}-500 rounded-xl flex items-center justify-center mb-4`}>
                            <IconComponent className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="font-bold text-gray-900 dark:text-white mb-2">{guide.title}</h3>
                          <p className={`text-sm text-${guide.color}-700 dark:text-${guide.color}-400 mb-4 flex items-center`}>
                            <Clock className="w-4 h-4 mr-1" />
                            {guide.estimatedTime}
                          </p>
                          <div className="space-y-2">
                            {guide.steps.map((step, index) => (
                              <div key={index} className="flex items-start space-x-2">
                                <span className={`flex-shrink-0 w-5 h-5 bg-${guide.color}-500 text-white text-xs rounded-full flex items-center justify-center font-medium`}>
                                  {index + 1}
                                </span>
                                <span className="text-sm text-gray-700 dark:text-gray-300">{step}</span>
                              </div>
                            ))}
                          </div>
                          <button className={`w-full mt-4 px-4 py-2 bg-${guide.color}-500 text-white rounded-lg hover:bg-${guide.color}-600 transition-colors`}>
                            Rehberi Başlat
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Video Tutorials Tab Content */}
            {activeTab === 'videos' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Video Eğitimler</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {videoTutorials.map((video) => (
                      <div key={video.id} className="group cursor-pointer">
                        <div className="relative rounded-xl overflow-hidden mb-4">
                          <img 
                            src={video.thumbnail} 
                            alt={video.title}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform"
                          />
                          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                              <PlayCircle className="w-8 h-8 text-gray-900" />
                            </div>
                          </div>
                          <div className="absolute bottom-3 right-3 bg-black/80 text-white px-2 py-1 rounded text-sm">
                            {video.duration}
                          </div>
                        </div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {video.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">{video.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center">
                            <Eye className="w-4 h-4 mr-1" />
                            {video.views}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {video.duration}
                          </span>
                        </div>
                      </div>
          ))}
        </div>
      </div>
              </div>
            )}

            {/* Support Options Tab Content */}
            {activeTab === 'support' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Destek Seçenekleri</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {supportOptions.map((option) => {
                      const IconComponent = option.icon;
                      return (
                        <div
                          key={option.title}
                          className={`p-6 rounded-xl border-2 transition-all hover:shadow-lg ${
                            option.primary
                              ? 'border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                          }`}
                        >
                          <div className="flex items-start space-x-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              option.primary ? 'bg-blue-500' : 'bg-gray-500'
                            }`}>
                              <IconComponent className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h3 className="font-bold text-gray-900 dark:text-white">{option.title}</h3>
                                <div className={`w-2 h-2 rounded-full ${
                                  option.status === 'online' ? 'bg-green-500' :
                                  option.status === 'available' ? 'bg-yellow-500' :
                                  'bg-gray-400'
                                }`}></div>
                              </div>
                              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">{option.description}</p>
                              <div className="space-y-1 text-sm">
                                <div className="flex items-center space-x-2">
                                  <Clock className="w-4 h-4 text-gray-400" />
                                  <span className="text-gray-600 dark:text-gray-400">{option.available}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RefreshCw className="w-4 h-4 text-gray-400" />
                                  <span className="text-gray-600 dark:text-gray-400">Yanıt süresi: {option.responseTime}</span>
                                </div>
                              </div>
                              <button className={`w-full mt-4 px-4 py-2 rounded-lg font-medium transition-colors ${
                                option.primary
                                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                              }`}>
                                {option.action}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Contact Form */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Bize Mesaj Gönderin</h3>
                  <form className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Ad Soyad
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Adınızı girin"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          E-posta
                        </label>
                        <input
                          type="email"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="E-posta adresinizi girin"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Konu
                      </label>
                      <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option>Teknik Destek</option>
                        <option>Hesap Sorunu</option>
                        <option>Faturalama</option>
                        <option>Özellik Talebi</option>
                        <option>Diğer</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mesajınız
                      </label>
                      <textarea
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Sorununuzu detaylı olarak açıklayın..."
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="w-full md:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <Send className="w-4 h-4" />
                      <span>Mesaj Gönder</span>
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Aradığınızı bulamadınız mı?</h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Uzman destek ekibimiz size yardımcı olmaya hazır. 7/24 destek hizmeti ile sorularınızı yanıtlıyoruz.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-6 py-3 bg-white text-blue-600 rounded-lg font-medium hover:bg-gray-100 transition-colors">
              Canlı Destek Başlat
            </button>
            <button className="px-6 py-3 bg-blue-700 text-white rounded-lg font-medium hover:bg-blue-800 transition-colors border border-blue-500">
              E-posta Gönder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpSupport;
